import { Entity } from '@core/entities/entity'
import { IPermitDraft } from './IPermitDraft'
import { PermitBase } from '@core/entities/permit/PermitBase'
import { plainToInstance, Type } from 'class-transformer'
import { User } from '@core/entities/user'
import { TemplateFormAnswers } from '@core/entities/template/TemplateFormAnswers'
import { BriefingSession } from '@core/entities/briefing'
import {
  ITemplateProjectLocations,
  TemplateProjectLocations,
} from '@core/entities/template/TemplateProjectLocations'
import { PermitTemplate } from '@core/entities/permit/PermitTemplate'
import {
  ITemplateQuestionGroup,
  TemplateQuestionGroup,
} from '@core/entities/template/TemplateQuestionGroup'
import { FeatureCollection, Feature, GeoJSON, Point, Polygon } from 'geojson'
import {
  getGeolocationAreas,
  getW3wLocation,
} from '@core/utils/geolocation-helpers'

export class PermitDraft extends Entity<IPermitDraft> {
  id: number
  draftId?: number
  draftName: string
  summary: string
  organisationId: number
  projectId: number
  projectName: string
  templateId: number
  templateVersionId: number
  @Type(() => PermitTemplate)
  template: PermitTemplate
  // currentTemplate is present only if there is a newer version of the template
  @Type(() => PermitTemplate)
  currentTemplate?: PermitTemplate
  location: string
  locationId: string
  geoJSON?: GeoJSON
  @Type(() => User)
  createdBy: User
  permitHolderId: number
  @Type(() => User)
  permitHolder: User
  startsOn: string
  endsOn: string
  participants: Array<{ id: number; firstName: string; lastName: string }>
  @Type(() => TemplateFormAnswers)
  authorisedPersonAnswers?: TemplateFormAnswers
  @Type(() => TemplateFormAnswers)
  permitHolderAnswers?: TemplateFormAnswers
  @Type(() => TemplateProjectLocations)
  projectLocations: TemplateProjectLocations
  permittedActions: Array<'DELETE' | 'EDIT' | 'READ'>

  formStartTime?: string
  formTrackingId?: string

  /**
   * @deprecated Use `permitHolderAnswersWrapper` instead.
   * Contains questions with answers for PERMIT_HOLDER initiated drafts.
   */
  @Type(() => TemplateQuestionGroup)
  body?: TemplateQuestionGroup[]

  /**
   * @deprecated Use `authorisedPersonAnswersWrapper` instead.
   * Contains questions with answers for AUTHORISED_PERSON initiated drafts.
   */
  @Type(() => TemplateQuestionGroup)
  authorisedPersonInitiateBody?: TemplateQuestionGroup[]

  hiddenQuestionIds?: string[]

  @Type(() => PermitBase)
  parentPermit?: PermitBase

  @Type(() => BriefingSession)
  briefingSession?: BriefingSession

  public static new(payload: unknown): PermitDraft {
    const entity = plainToInstance(PermitDraft, payload)
    return entity
  }

  /**
   * Checks if this draft was initiated by an Authorised Person.
   */
  isAuthorisedPersonFlow(): boolean {
    return this.template?.currentVersion?.initiatorRole === 'AUTHORISED_PERSON'
  }

  /**
   * Returns the questions for this draft based on initiator role.
   *
   * - AUTHORISED_PERSON flow: returns `authorisedPersonInitiateBody`
   * - PERMIT_HOLDER flow: returns `body`
   */
  getQuestions(): ITemplateQuestionGroup[] | undefined {
    if (this.isAuthorisedPersonFlow()) {
      return this.authorisedPersonInitiateBody?.map((q) => q.toPlain())
    }

    return this.body?.map((q) => q.toPlain())
  }

  /**
   * Returns the answered questions for this draft based on initiator role.
   *
   * - AUTHORISED_PERSON flow: returns `authorisedPersonAnswers.answers`
   * - PERMIT_HOLDER flow: returns `permitHolderAnswers.answers`
   */
  getAnsweredQuestions(): ITemplateQuestionGroup[] | undefined {
    if (this.isAuthorisedPersonFlow()) {
      return this.authorisedPersonAnswers?.answers?.map((q) => q.toPlain())
    }

    return this.permitHolderAnswers?.answers?.map((q) => q.toPlain())
  }

  getProjectLocations(): ITemplateProjectLocations {
    if (this.projectLocations?.locations?.length > 0) {
      return this.projectLocations.toPlain()
    }

    if (this.location) {
      return {
        locations: [
          {
            locationName: this.location,
            locationNodeId: '',
          },
        ],
      }
    }

    return { locations: [] }
  }

  getW3wLocation(): Feature<Point> {
    return getW3wLocation(this.geoJSON)
  }

  getGeolocationAreas(): FeatureCollection<Polygon> {
    return getGeolocationAreas(this.geoJSON)
  }
}
